import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
import { generatedFileResponseSchema } from "./imports.js";
/**
 * Barcode labels (C-11, B-70, docs/specs/catalog.md "Barcode labels"): a PDF of stickers for chosen variants, on A4
 * sticker sheets or a label printer's roll.
 */
/**
 * Sticker sizes in millimetres. A4 sheets: columns × rows from the top-left margin with the gaps of the common
 * Bangladeshi/Avery-style sheets; rolls: one label per page, the page the label's size (thermal label printers).
 */
export const LABEL_LAYOUTS = {
    a4_65: {
        kind: "sheet",
        widthMm: 38.1,
        heightMm: 21.2,
        columns: 5,
        rows: 13,
        marginTopMm: 10.7,
        marginLeftMm: 4.65,
        gapXMm: 2.5,
        gapYMm: 0,
    },
    a4_40: {
        kind: "sheet",
        widthMm: 52.5,
        heightMm: 29.7,
        columns: 4,
        rows: 10,
        marginTopMm: 0,
        marginLeftMm: 0,
        gapXMm: 0,
        gapYMm: 0,
    },
    a4_24: {
        kind: "sheet",
        widthMm: 70,
        heightMm: 37,
        columns: 3,
        rows: 8,
        marginTopMm: 0.5,
        marginLeftMm: 0,
        gapXMm: 0,
        gapYMm: 0,
    },
    roll_38x25: {
        kind: "roll",
        widthMm: 38,
        heightMm: 25,
        columns: 1,
        rows: 1,
        marginTopMm: 0,
        marginLeftMm: 0,
        gapXMm: 0,
        gapYMm: 0,
    },
    roll_50x25: {
        kind: "roll",
        widthMm: 50,
        heightMm: 25,
        columns: 1,
        rows: 1,
        marginTopMm: 0,
        marginLeftMm: 0,
        gapXMm: 0,
        gapYMm: 0,
    },
    roll_50x30: {
        kind: "roll",
        widthMm: 50,
        heightMm: 30,
        columns: 1,
        rows: 1,
        marginTopMm: 0,
        marginLeftMm: 0,
        gapXMm: 0,
        gapYMm: 0,
    },
};
export const LABEL_LAYOUT_VALUES = Object.keys(LABEL_LAYOUTS);
export const LABELS_MAX_ITEMS = 200;
export const LABELS_MAX_COPIES = 500;
export const LABELS_MAX_TOTAL = 2_000;
export const labelFieldsSchema = z
    .object({
    businessName: z.boolean(),
    productName: z.boolean(),
    price: z.boolean(),
    mrp: z.boolean(),
    sku: z.boolean(),
})
    .strict();
export const barcodeLabelsRequestSchema = z
    .object({
    items: z
        .array(z.object({ variantId: z.uuid(), copies: z.int().min(1).max(LABELS_MAX_COPIES) }).strict())
        .min(1)
        .max(LABELS_MAX_ITEMS)
        .refine((items) => items.reduce((sum, item) => sum + item.copies, 0) <= LABELS_MAX_TOTAL, {
        message: `At most ${String(LABELS_MAX_TOTAL)} labels at a time`,
    }),
    layout: z.enum(LABEL_LAYOUT_VALUES),
    fields: labelFieldsSchema,
    /** The price at this branch (its override, else the base price); absent: the base price. */
    locationId: z.uuid().optional(),
    /** A4 only: stickers already used at the top of the first sheet. */
    skip: z.int().min(0).max(64).default(0),
})
    .strict();
export const printBarcodeLabels = defineEndpoint({
    tag: "catalog",
    name: "printBarcodeLabels",
    method: "POST",
    path: "/v1/barcode-labels",
    access: { permission: "catalog.view" },
    summary: "A PDF of barcode stickers for chosen variants (signed URL, kept 7 days)",
    body: barcodeLabelsRequestSchema,
    status: 201,
    response: generatedFileResponseSchema,
    errors: ["NO_BARCODE"],
});
//# sourceMappingURL=catalog-labels.js.map